import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import FullScreenPanel from './UI/FullScreenPanel';
import ClientLoanHistory from './ClientLoanHistory';
import OpenFullPageLink from './OpenFullPageLink';
import DisplayField, { FieldGroup } from './UI/DisplayField';
import { toCurrency } from '../utils/adminMetrics';

// Quick look at a client (staff): profile summary and their loans, read from
// GET /api/clients/:id/profile and /api/clients/:id/loans. Editing and the
// other actions stay on the Clients page ("Open full page").
const ClientDetailsModal = ({ clientId, name = '', onClose, onOpenFullPage = null }) => {
  const [profile, setProfile] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    setLoadingProfile(true);
    setLoadingLoans(true);
    setError('');
    axios.get(apiUrl(`/api/clients/${clientId}/profile`), { headers })
      .then((res) => { if (!cancelled) setProfile(res.data || null); })
      .catch(() => { if (!cancelled) setError('The client profile could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoadingProfile(false); });
    axios.get(apiUrl(`/api/clients/${clientId}/loans`), { headers })
      .then((res) => { if (!cancelled) setLoans(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setLoans([]); })
      .finally(() => { if (!cancelled) setLoadingLoans(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const p = profile?.personal_info || {};
  const o = profile?.occupation_info || {};
  const b = profile?.bank_info || {};
  const fullName = p.full_name || [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ') || name;

  return (
    <FullScreenPanel
      isOpen
      onClose={onClose}
      title="Client Details"
      subtitle={[fullName, p.client_code].filter(Boolean).join(' · ')}
      headerRight={onOpenFullPage ? <OpenFullPageLink onClick={onOpenFullPage} /> : null}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 text-sm">{error}</div>
      )}
      {loadingProfile ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">Loading client profile...</p>
        </div>
      ) : profile ? (
        // The same groups and fields as Loan Details.
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
          <FieldGroup title="Personal" tone="blue" wide>
            <DisplayField label="Full name">{fullName}</DisplayField>
            <DisplayField label="Client code">{p.client_code}</DisplayField>
            <DisplayField label="ID / Passport No">{p.id_passport_no}</DisplayField>
            <DisplayField label="Phone number">{p.phone_number}</DisplayField>
            <DisplayField label="Email">{p.email}</DisplayField>
            <DisplayField label="Residential address">{p.residential_address}</DisplayField>
            <DisplayField label="City / Town">{p.city}</DisplayField>
            <DisplayField label="Marital status">{p.marital_status}</DisplayField>
            <DisplayField label="Gender">{p.gender}</DisplayField>
          </FieldGroup>
          <FieldGroup title="Occupation" tone="emerald">
            <DisplayField label="Occupation">{o.occupation}</DisplayField>
            <DisplayField label="Employer">{o.employer_name}</DisplayField>
            <DisplayField label="Employer phone">{o.employer_phone}</DisplayField>
            <DisplayField label="Take-home pay">{o.salary != null && o.salary !== '' ? toCurrency(o.salary) : null}</DisplayField>
          </FieldGroup>
          <FieldGroup title="Bank" tone="teal">
            <DisplayField label="Bank">{b.bank_name}</DisplayField>
            <DisplayField label="Branch code">{b.branch_code}</DisplayField>
            <DisplayField label="Account number">{b.account_number}</DisplayField>
            <DisplayField label="Account type">{b.account_type}</DisplayField>
          </FieldGroup>
        </div>
      ) : null}
      <h3 className="mt-6 mb-2 text-sm font-semibold text-gray-800 dark:text-slate-100">Loans</h3>
      <ClientLoanHistory
        clientLoans={loans}
        loadingLoans={loadingLoans}
        clientName={fullName}
        clientCode={p.client_code || ''}
      />
    </FullScreenPanel>
  );
};

export default ClientDetailsModal;
